import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getCateringOrderStatusPublic } from "../../api/get/getCateringOrderStatusPublic";

const CateringOrderStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract URL parameters
  const orderId = searchParams.get("order_id");
  const orderNo = searchParams.get("order_no");
  const referenceNumber = searchParams.get("reference_number");
  const initialOrderStatus = searchParams.get("order_status");
  
  // Component state
  const [orderStatus, setOrderStatus] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOrderConfirmed, setIsOrderConfirmed] = useState(false);

  // Determine if this is a success or failure page based on URL path
  const isSuccessPage = window.location.pathname.includes('catering-order-complete');
  const isFailurePage = window.location.pathname.includes('catering-order-failed');

  // Fetch order status once
  useEffect(() => {
    const fetchOrderStatus = async () => {
      if (!orderNo || !referenceNumber) {
        setError("Missing order information");
        setLoading(false);
        return;
      }

      try {
        const response = await getCateringOrderStatusPublic(orderNo, referenceNumber);
        
        if (response && response.data) {
          setOrderDetails(response.data);
          const status = response.data.catering_order_status || response.data.order_status || response.data.status;
          setOrderStatus(status);
          
          // Determine if order is confirmed based on status
          const confirmedStatuses = ['pending payment', 'confirmed', 'payment successful', 'submitted', 'processing', 'paid'];
          const isConfirmed = confirmedStatuses.some(s => status?.toLowerCase().includes(s.toLowerCase()));
          setIsOrderConfirmed(isConfirmed);
        }
        
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error fetching order status:", err);
        setError("Failed to fetch order status");
        setLoading(false);
        setIsOrderConfirmed(false);
      }
    };

    // Single API call
    fetchOrderStatus();
  }, [orderNo, referenceNumber]);

  const handleClose = () => {
    // For customers, we can close the window or go to a public page
    window.close();
  };

  const handleViewReceipt = () => {
    // Navigate to receipt page or open receipt modal
    console.log("View receipt for order:", orderNo);
  };

  const handleCall = () => {
    window.open('tel:1818555', '_self');
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/1818555', '_blank');
  };

  const getStatusIcon = () => {
    if (!isOrderConfirmed || error) {
      return (
        <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }
    
    return (
      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  };

  const getMainMessage = () => {
    if (!isOrderConfirmed || error) {
      return "Order Failed";
    }
    return "Order Confirmed";
  };

  const getSubMessage = () => {
    if (!isOrderConfirmed || error) {
      return "Payment Unsuccessful - There was an issue processing your catering order.";
    }
    return "Payment Successful - Your catering order has been confirmed.";
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#86604D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm p-6 text-center">
        <div className="flex justify-center mb-3">
          <img 
            src="/Images/Logo.png" 
            alt="Seazen Logo" 
            className="h-12 w-auto"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <h1 className="text-xl font-bold text-gray-800">Order Status</h1>
        <p className="text-sm text-gray-600 mt-1">Track your catering order</p>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 max-w-md mx-auto w-full">
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          
          {/* Status Header */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              {getStatusIcon()}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {getMainMessage()}
              </h2>
              <p className="text-gray-600">
                {getSubMessage()}
              </p>
            </div>
          </div>

          {/* Order Details */}
          {orderDetails && (
            <div className="space-y-6">
              
              {/* Restaurant Info */}
              <div className="text-center border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {orderDetails.restaurant_name_en || 'Restaurant'}
                </h3>
                <p className="text-sm text-gray-600">Catering Service</p>
              </div>

              {/* Order Information */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Order Number:</span>
                  <span className="font-semibold text-gray-800">{orderDetails.order_no}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-semibold text-gray-800">{orderDetails.order_id}</span>
                </div>

                {referenceNumber && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-semibold text-gray-800">{referenceNumber}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Service Type:</span>
                  <span className="font-semibold text-gray-800 capitalize">{orderDetails.service_type}</span>
                </div>

                {orderDetails.no_of_pax && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Number of PAX:</span>
                    <span className="font-semibold text-gray-800">{orderDetails.no_of_pax}</span>
                  </div>
                )}

                {orderDetails.service_item_date && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Service Date:</span>
                    <span className="font-semibold text-gray-800">{orderDetails.service_item_date}</span>
                  </div>
                )}

                {orderDetails.service_item_slot && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Service Time:</span>
                    <span className="font-semibold text-gray-800">{orderDetails.service_item_slot}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isOrderConfirmed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {orderStatus || (isOrderConfirmed ? 'Confirmed' : 'Failed')}
                  </span>
                </div>
              </div>

              {/* Error Message for Failed Orders */}
              {(!isOrderConfirmed || error) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-red-800">Payment Issue</h4>
                      <p className="text-sm text-red-700 mt-1">
                        {error || "There was an issue processing the payment for your catering order. Please contact support for assistance."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Message for Confirmed Orders */}
              {isOrderConfirmed && !error && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-green-800">Order Confirmed</h4>
                      <p className="text-sm text-green-700 mt-1">
                        Your catering order has been successfully confirmed. We will contact you shortly with further details.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Buttons */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <button 
              onClick={handleCall}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium flex items-center justify-center space-x-2 hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>1818555</span>
            </button>
            
            <button 
              onClick={handleWhatsApp}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium flex items-center justify-center space-x-2 hover:bg-green-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              <span>Send Message</span>
            </button>
          </div>
        </div>
      </div>

      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6">
        <button className="w-12 h-12 bg-[#86604D] rounded-full shadow-lg flex items-center justify-center hover:bg-[#6B4A3A] transition-colors">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CateringOrderStatus;
